import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ReactNode } from 'react';
import type {
  AnonymityResponse,
  ClubMember,
  Coupon,
  PublicCreditPack,
  CreditTransaction,
  EconomyNaming,
  GameProgression,
  GameView,
  PlayerStats,
  PublicWheelConfig,
  ShopOrder,
  SpinOutcome,
  SpinRecord,
  SpinResponse,
  WheelId,
  WheelStatus,
} from '@shared';
import { request } from '../lib/spoylApi';

/**
 * The player's view of the game, mirrored from `GET /api/game`.
 *
 * The type is the point. Everything here is `GameView`, which has no field for
 * `realChance` anywhere in it — so no player-facing component can render the
 * true odds, or the balance of anyone else, because it was never given them.
 * The owner console has its own store; the two never meet.
 */

/** How often the mirror re-reads itself. See the effect in `GameProvider`. */
const POLL_INTERVAL_MS = 10_000;

const EMPTY_WHEEL: PublicWheelConfig = {
  name: '',
  description: '',
  enabled: false,
  spinCost: 0,
  cooldownHours: 0,
  segments: [],
};

const EMPTY_STATUS: WheelStatus = {
  enabled: false,
  valid: false,
  cost: 0,
  affordable: false,
  cooldownHours: 0,
  onCooldown: false,
  nextAt: null,
  canSpin: false,
};

/**
 * The house names, used until the server's own land.
 *
 * They match the defaults in `shared/src/economy/state.ts`, so a creator who has
 * renamed nothing sees the same words before and after the first response — and
 * one who has renamed everything sees the rename a few hundred milliseconds in,
 * rather than a blank unit.
 */
const DEFAULT_NAMING: EconomyNaming = {
  creditName: 'Credit',
  creditNamePlural: 'Credits',
  xpName: 'XP',
};

/** What the UI renders before the first response lands: a wheel that can't spin. */
const EMPTY_VIEW: GameView = {
  balance: 0,
  naming: DEFAULT_NAMING,
  stats: { balance: 0, spins: 0, wins: 0, spent: 0, rewardValue: 0 },
  wheels: {
    main: { config: EMPTY_WHEEL, status: EMPTY_STATUS },
    daily: { config: EMPTY_WHEEL, status: EMPTY_STATUS },
    scratch: { config: EMPTY_WHEEL, status: EMPTY_STATUS },
  },
  spins: [],
  transactions: [],
  shop: [],
  orders: [],
  coupons: [],
  progression: { xp: 0, anonymous: false, boost: null, track: [] },
  club: [],
};

interface GameValue {
  loading: boolean;
  error: string | null;
  balance: number;
  /**
   * What this creator calls her money and her gauge. Every player-facing string
   * that names either reads it from here — see {@link useNaming}.
   */
  naming: EconomyNaming;
  /** Totals over this player's whole history, not just the rows they were sent. */
  stats: PlayerStats;
  /** The wheel as the owner chose to advertise it. Never the odds it runs on. */
  wheel: (id: WheelId) => PublicWheelConfig;
  wheelStatus: (id: WheelId) => WheelStatus;
  /** This player's spins, newest first. */
  spins: SpinRecord[];
  /** This player's credit movements, newest first. */
  transactions: CreditTransaction[];
  /** The ways to earn credits. No price — Spoyl sells nothing. */
  shop: PublicCreditPack[];
  /** This player's credit claims, newest first. */
  orders: ShopOrder[];
  /** This player's coupons, spent ones included, newest first. */
  coupons: Coupon[];
  /** Their place on the status ladder: XP, anonymity, and the visible track. */
  progression: GameProgression;
  /** The whole club by status, best first. Anonymous members carry no name. */
  club: ClubMember[];
  /** Shows or hides this player's name in the club. */
  setAnonymous: (anonymous: boolean) => Promise<void>;
  /**
   * Draws a result on the server and charges for it, but keeps the resulting
   * view to itself so the rest of the app can't show the outcome early. Call
   * {@link commitSpin} once the player has been told.
   */
  spin: (id: WheelId) => Promise<SpinOutcome>;
  /** Publishes the view held by {@link spin}. A no-op when none is pending. */
  commitSpin: () => void;
  refresh: () => Promise<void>;
}

const GameContext = createContext<GameValue | null>(null);

export function GameProvider({ children }: { children: ReactNode }) {
  const [view, setView] = useState<GameView>(EMPTY_VIEW);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * A view the server has already committed but the player has not been shown.
   *
   * Held in a ref rather than state on purpose: nothing may re-render off it, or
   * the new balance would appear in the top bar while the wheel is still
   * turning — which is exactly the bug the deferred commit exists to prevent.
   */
  const pending = useRef<GameView | null>(null);

  /**
   * Re-reads the view. `silent` suppresses the error banner, which is what the
   * background poll wants: one dropped request on a flaky connection is not
   * news, and the next tick will have fixed it.
   */
  const read = useCallback(async (silent: boolean) => {
    try {
      const next = await request<GameView>('/game');
      // A refresh landing mid-spin must not undo the hold: the pending view is
      // newer than anything a plain read can return.
      if (!pending.current) setView(next);
      setError(null);
    } catch (cause) {
      if (!silent) {
        setError(cause instanceof Error ? cause.message : 'Could not reach the game server.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const refresh = useCallback(() => read(false), [read]);

  /**
   * The game re-reads itself on a timer, and again whenever the player comes
   * back to the window.
   *
   * The client is a mirror of state it does not own: credits granted from the
   * console, a reward marked delivered, a wheel switched off — all of it happens
   * elsewhere and none of it reaches this process on its own. Without the poll
   * the only way to see any of it was to relaunch the activity, which is exactly
   * what players were doing.
   *
   * A plain interval rather than a socket: the payload is small, one guild is
   * not many players, and a websocket through Discord's proxy is a whole
   * failure mode for a feature that is fine being ten seconds late.
   */
  useEffect(() => {
    void read(false);

    const timer = setInterval(() => void read(true), POLL_INTERVAL_MS);
    const onVisible = () => {
      if (!document.hidden) void read(true);
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [read]);

  const spin = useCallback(async (id: WheelId): Promise<SpinOutcome> => {
    try {
      const { outcome, view: next } = await request<SpinResponse>('/spin', {
        method: 'POST',
        body: JSON.stringify({ wheelId: id }),
      });

      if (outcome.ok) {
        pending.current = next;
      } else {
        // Nothing was charged, but the refusal itself is news — the wheel may
        // have been switched off since the page loaded.
        setView(next);
      }
      setError(null);
      return outcome;
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'The spin could not be sent.');
      return { ok: false, reason: 'wheel-invalid', balance: 0 };
    }
  }, []);

  const commitSpin = useCallback(() => {
    const next = pending.current;
    if (!next) return;
    pending.current = null;
    setView(next);
  }, []);

  /**
   * Publishes a view that came back from an action other than a spin.
   *
   * If a spin is mid-flight its result is already in this payload — the server
   * committed it six seconds ago — so publishing would spoil the wheel. It goes
   * into the hold instead, and comes out at the reveal like everything else.
   */
  const publish = useCallback((next: GameView) => {
    if (pending.current) pending.current = next;
    else setView(next);
  }, []);

  const setAnonymous = useCallback(
    async (anonymous: boolean): Promise<void> => {
      try {
        const { view: next } = await request<AnonymityResponse>('/anonymity', {
          method: 'POST',
          body: JSON.stringify({ anonymous }),
        });
        publish(next);
        setError(null);
      } catch (cause) {
        setError(cause instanceof Error ? cause.message : 'The change could not be saved.');
      }
    },
    [publish],
  );

  const value = useMemo<GameValue>(
    () => ({
      loading,
      error,
      balance: view.balance,
      naming: view.naming,
      stats: view.stats,
      wheel: (id) => view.wheels[id].config,
      wheelStatus: (id) => view.wheels[id].status,
      spins: view.spins,
      transactions: view.transactions,
      shop: view.shop,
      orders: view.orders,
      coupons: view.coupons,
      progression: view.progression,
      club: view.club,
      setAnonymous,
      spin,
      commitSpin,
      refresh,
    }),
    [loading, error, view, setAnonymous, spin, commitSpin, refresh],
  );

  return <GameContext value={value}>{children}</GameContext>;
}

export function useGame(): GameValue {
  const value = useContext(GameContext);
  if (!value) {
    throw new Error('useGame must be used inside a GameProvider');
  }
  return value;
}

/**
 * The creator's words for her money and her gauge.
 *
 * A player-facing component must never reach into the owner's store for these —
 * `useEconomy` throws for a player, by design. The labels travel on the same
 * projection as the balance, so this is the one place they come from.
 */
export function useNaming(): EconomyNaming {
  return useGame().naming;
}
